import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';

import { PriceAverageComponent } from '@app/pages/guides/priceAverage/priceAverage.component';
import { ReviewOverviewComponent } from '@components/reviewOverview/reviewOverview.component';
import { ReviewAggregationForTagResponse } from '@components/reviewOverview/interface/reviewAggretationResponse.type.interface';
import { MiniVertical } from '@app/services/http/mini-verticals.service';
import { Task } from '@models/task.model';

export interface ReviewSchemaData {
  averageRating: number;
  reviewCount: number;
  hasReviews: boolean;
}

@Component({
  selector: 'app-mini-vertical-price-review',
  templateUrl: './mini-vertical-price-review.component.html',
  styleUrls: ['./mini-vertical-price-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PriceAverageComponent, ReviewOverviewComponent],
})
export class MiniVerticalPriceReviewComponent implements OnInit {
  @Input() miniVertical!: MiniVertical;
  @Input() priceGuideTitle?: string;
  @Input() priceGuideLink?: string | null;
  @Output() reviewDataCalculated = new EventEmitter<ReviewSchemaData>();

  /**
   * The resolved band from the parent — the trusted live band when one
   * exists, else `price_guide`. It is the same value the `<title>`, the meta
   * description and the JSON-LD quote, so the visible widget cannot disagree
   * with any of them. When no band is passed, the raw guide is the fallback.
   */
  @Input() priceLow: number = 0;
  @Input() priceMedium: number = 0;
  @Input() priceHigh: number = 0;

  reviewCount!: ReviewAggregationForTagResponse;
  tasks: Task[] = [];
  hasReviews: boolean = false;
  hasPrices: boolean = false;

  ngOnInit() {
    this.calculatePrices();
    this.calculateReviewStats();
  }

  private calculatePrices(): void {
    const hasResolvedBand =
      this.priceLow > 0 || this.priceMedium > 0 || this.priceHigh > 0;

    if (!hasResolvedBand && this.miniVertical?.price_guide) {
      this.priceLow = this.miniVertical.price_guide.min || 0;
      this.priceMedium = this.miniVertical.price_guide.middle || 0;
      this.priceHigh = this.miniVertical.price_guide.max || 0;
    }

    this.hasPrices =
      this.priceLow > 0 || this.priceMedium > 0 || this.priceHigh > 0;
  }

  private calculateReviewStats(): void {
    if (
      !this.miniVertical?.similarTasks ||
      this.miniVertical.similarTasks.length === 0
    ) {
      this.hasReviews = false;
      return;
    }

    // Extract all user ratings from similarTasks offers
    const userRatings: number[] = [];
    this.miniVertical.similarTasks.forEach((task) => {
      if (task.offers && Array.isArray(task.offers)) {
        task.offers.forEach((offer: any) => {
          if (offer.user && offer.user.rating && offer.user.rating > 0) {
            userRatings.push(offer.user.rating);
          }
        });
      }
    });

    if (userRatings.length === 0) {
      this.hasReviews = false;
      return;
    }

    // Calculate average rating
    const totalRating = userRatings.reduce((sum, rating) => sum + rating, 0);
    const averageRating = totalRating / userRatings.length;

    // Use the number of ratings multiplied by 29 as our "review count"
    const totalReviews = userRatings.length * 29;

    // Create realistic distribution based on average rating
    const distribution = this.createReviewDistribution(
      averageRating,
      totalReviews,
    );

    this.reviewCount = {
      average: parseFloat(averageRating.toFixed(1)),
      count: {
        total: totalReviews,
        perRating: distribution,
      },
    };

    // Use taskExamples if available, otherwise similarTasks
    this.tasks = (this.miniVertical.taskExamples ||
      this.miniVertical.similarTasks) as Task[];
    this.hasReviews = true;

    // Emit review data for schema generation
    this.emitReviewData();
  }

  /**
   * Emit review data to parent component for schema generation
   */
  private emitReviewData(): void {
    if (this.hasReviews && this.reviewCount) {
      this.reviewDataCalculated.emit({
        averageRating: this.reviewCount.average,
        reviewCount: this.reviewCount.count.total,
        hasReviews: true,
      });
    } else {
      this.reviewDataCalculated.emit({
        averageRating: 0,
        reviewCount: 0,
        hasReviews: false,
      });
    }
  }

  /**
   * Creates a realistic distribution of reviews based on average rating
   * Higher average = more 4-5 star reviews, lower average = more spread out
   */
  private createReviewDistribution(
    averageRating: number,
    totalReviews: number,
  ): { 1: string; 2: string; 3: string; 4: string; 5: string } {
    let distribution = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    };

    // Create distribution based on average rating
    if (averageRating >= 4.5) {
      // Excellent ratings: heavily weighted toward 5 stars
      distribution[5] = Math.round(totalReviews * 0.75); // 75% five stars
      distribution[4] = Math.round(totalReviews * 0.2); // 20% four stars
      distribution[3] = Math.round(totalReviews * 0.03); // 3% three stars
      distribution[2] = Math.round(totalReviews * 0.01); // 1% two stars
      distribution[1] = Math.round(totalReviews * 0.01); // 1% one star
    } else if (averageRating >= 4.0) {
      // Very good ratings
      distribution[5] = Math.round(totalReviews * 0.6); // 60% five stars
      distribution[4] = Math.round(totalReviews * 0.3); // 30% four stars
      distribution[3] = Math.round(totalReviews * 0.07); // 7% three stars
      distribution[2] = Math.round(totalReviews * 0.02); // 2% two stars
      distribution[1] = Math.round(totalReviews * 0.01); // 1% one star
    } else if (averageRating >= 3.5) {
      // Good ratings
      distribution[5] = Math.round(totalReviews * 0.45); // 45% five stars
      distribution[4] = Math.round(totalReviews * 0.35); // 35% four stars
      distribution[3] = Math.round(totalReviews * 0.12); // 12% three stars
      distribution[2] = Math.round(totalReviews * 0.05); // 5% two stars
      distribution[1] = Math.round(totalReviews * 0.03); // 3% one star
    } else if (averageRating >= 3.0) {
      // Average ratings
      distribution[5] = Math.round(totalReviews * 0.3); // 30% five stars
      distribution[4] = Math.round(totalReviews * 0.35); // 35% four stars
      distribution[3] = Math.round(totalReviews * 0.2); // 20% three stars
      distribution[2] = Math.round(totalReviews * 0.1); // 10% two stars
      distribution[1] = Math.round(totalReviews * 0.05); // 5% one star
    } else {
      // Below average ratings
      distribution[5] = Math.round(totalReviews * 0.2); // 20% five stars
      distribution[4] = Math.round(totalReviews * 0.25); // 25% four stars
      distribution[3] = Math.round(totalReviews * 0.25); // 25% three stars
      distribution[2] = Math.round(totalReviews * 0.2); // 20% two stars
      distribution[1] = Math.round(totalReviews * 0.1); // 10% one star
    }

    // Adjust to make sure total equals totalReviews
    const currentTotal = Object.values(distribution).reduce(
      (sum, val) => sum + val,
      0,
    );
    const difference = totalReviews - currentTotal;

    // Add/subtract the difference to the most common rating
    if (difference !== 0) {
      const topRating = averageRating >= 4 ? 5 : 4;
      distribution[topRating] += difference;
    }

    return {
      1: distribution[1].toString(),
      2: distribution[2].toString(),
      3: distribution[3].toString(),
      4: distribution[4].toString(),
      5: distribution[5].toString(),
    };
  }
}
